import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  FileText, DollarSign, AlertTriangle, ArrowRight,
  ShieldAlert, Wallet, Receipt, TrendingUp, TrendingDown, BarChart3, Clock, Settings, Loader2,
} from 'lucide-react';
import { KpiCard } from './KpiCard';
import api from '../../lib/api';
import { toast } from 'sonner';
import { OCSinFacturaAlertWidget } from '../../components/dashboard/OCSinFacturaAlertWidget';
import { ChequesEstaSemanaWidget } from '../../components/dashboard/ChequesEstaSemanaWidget';

const fmt = (v) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(v || 0);
const fmtNum = (num) => Math.round(num || 0).toLocaleString('es-CL');

export function DashboardContabilidad({ data }) {
  const [ncData, setNcData] = useState(null);
  const [ncLoading, setNcLoading] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [diasConfig, setDiasConfig] = useState(5);
  const [savingConfig, setSavingConfig] = useState(false);

  useEffect(() => {
    loadNcPendientes();
  }, []);

  const loadNcPendientes = async () => {
    setNcLoading(true);
    try {
      const res = await api.get('/contabilidad/nc-pendientes');
      setNcData(res.data);
      setDiasConfig(res.data.dias_alerta_nc || 5);
    } catch (err) { console.error('caught:', err); }
    finally { setNcLoading(false); }
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await api.post('/contabilidad/nc-config', { dias_alerta_nc: diasConfig });
      toast.success(`Alerta NC configurada a ${diasConfig} días`);
      setShowConfig(false);
      loadNcPendientes();
    } catch (err) {
      toast.error('Error guardando configuración');
    } finally { setSavingConfig(false); }
  };

  if (!data) return null;
  return (
    <div className="space-y-6" data-testid="dashboard-contabilidad">
      {/* Iter 286.d — Alerta de cheques por pagar esta semana (solo aparece si hay algo) */}
      <ChequesEstaSemanaWidget />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-1">
              <Wallet className="w-5 h-5 text-red-500" />
              <span className="text-xs font-medium uppercase text-muted-foreground">Cuentas por Pagar</span>
            </div>
            <p className="text-3xl font-bold text-red-700" data-testid="ctas-por-pagar">{fmt(data.cuentas_por_pagar)}</p>
            <p className="text-xs text-muted-foreground mt-1">{data.bills_pendientes} facturas pendientes</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-1">
              <DollarSign className="w-5 h-5 text-green-500" />
              <span className="text-xs font-medium uppercase text-muted-foreground">Cuentas por Cobrar</span>
            </div>
            <p className="text-3xl font-bold text-green-700" data-testid="ctas-por-cobrar">{fmt(data.cuentas_por_cobrar)}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-2">
              <BarChart3 className="w-5 h-5 text-blue-500" />
              <span className="text-xs font-medium uppercase text-muted-foreground">Ventas (Antes de Impuestos)</span>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{data.mes_actual}</span>
                <span className="text-lg font-bold text-blue-700" data-testid="ventas-actual">{fmt(data.ventas_mes_actual)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{data.mes_anterior}</span>
                <span className="text-sm text-gray-500">{fmt(data.ventas_mes_anterior)}</span>
              </div>
              {data.ventas_mes_anterior > 0 && (
                <div className="flex items-center gap-1 mt-1">
                  {data.ventas_mes_actual >= data.ventas_mes_anterior
                    ? <TrendingUp className="w-3.5 h-3.5 text-green-500" />
                    : <TrendingDown className="w-3.5 h-3.5 text-red-500" />}
                  <span className={`text-[11px] font-medium ${data.ventas_mes_actual >= data.ventas_mes_anterior ? 'text-green-600' : 'text-red-600'}`}>
                    {Math.abs(Math.round(((data.ventas_mes_actual / data.ventas_mes_anterior) - 1) * 100))}%
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-l-4 border-l-orange-400">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-2">
              <Receipt className="w-5 h-5 text-orange-500" />
              <span className="text-xs font-medium uppercase text-muted-foreground">Facturacion Compras</span>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{data.mes_actual}</span>
                <span className="text-lg font-bold text-orange-700" data-testid="compras-actual">{fmt(data.compras_mes_actual)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{data.mes_anterior}</span>
                <span className="text-sm text-gray-500">{fmt(data.compras_mes_anterior)}</span>
              </div>
              {data.compras_mes_anterior > 0 && (
                <div className="flex items-center gap-1 mt-1">
                  {data.compras_mes_actual >= data.compras_mes_anterior
                    ? <TrendingUp className="w-3.5 h-3.5 text-green-500" />
                    : <TrendingDown className="w-3.5 h-3.5 text-red-500" />}
                  <span className={`text-[11px] font-medium ${data.compras_mes_actual >= data.compras_mes_anterior ? 'text-green-600' : 'text-red-600'}`}>
                    {Math.abs(Math.round(((data.compras_mes_actual / data.compras_mes_anterior) - 1) * 100))}%
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-indigo-400">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-2">
              <BarChart3 className="w-5 h-5 text-indigo-500" />
              <span className="text-xs font-medium uppercase text-muted-foreground">Margen (Ventas - Compras)</span>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{data.mes_actual}</span>
                <span className={`text-lg font-bold ${(data.ventas_mes_actual - data.compras_mes_actual) >= 0 ? 'text-green-700' : 'text-red-700'}`} data-testid="margen-actual">
                  {fmt(data.ventas_mes_actual - data.compras_mes_actual)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{data.mes_anterior}</span>
                <span className="text-sm text-gray-500">{fmt(data.ventas_mes_anterior - data.compras_mes_anterior)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard title="Pendientes A/R" value={data.total_pendientes_ar} icon={FileText} color="text-indigo-600" bgColor="bg-indigo-50" link="/contabilidad/acepta-rechazo-dte" />
        <KpiCard title="Urgentes SII" value={data.total_urgentes_sii} icon={ShieldAlert} color="text-red-600" bgColor="bg-red-50" link="/contabilidad/acepta-rechazo-dte" urgent={data.total_urgentes_sii > 0} />
        <KpiCard title="Lotes Contabilizar" value={data.lotes_por_contabilizar} icon={Receipt} color="text-orange-600" bgColor="bg-orange-50" link="/contabilidad/facturas-contabilizar" />
        <KpiCard title="Sin Contabilizar" value={data.sin_contabilizar || 0} icon={FileText} color="text-yellow-600" bgColor="bg-yellow-50" link="/contabilidad/registro-dte" subtitle={data.sin_contabilizar_15d > 0 ? `${data.sin_contabilizar_15d} con más de 15 días` : undefined} />
        {data.sin_contabilizar_15d > 0 && (
          <KpiCard title="+15 Días sin Contab." value={data.sin_contabilizar_15d} icon={Clock} color="text-red-600" bgColor="bg-red-50" link="/contabilidad/registro-dte" urgent />
        )}
        <KpiCard title="NC Pendientes" value={data.nc_pendientes || 0} icon={AlertTriangle} color="text-red-600" bgColor="bg-red-50" link="/contabilidad/registro-dte" subtitle={data.nc_en_alerta > 0 ? `${data.nc_en_alerta} en alerta (>${data.dias_alerta_nc || 5}d)` : undefined} urgent={data.nc_en_alerta > 0} />
        <KpiCard title="Rechazos SII" value={data.rechazos_sii_pendientes} icon={AlertTriangle} color="text-amber-600" bgColor="bg-amber-50" link="/contabilidad/acepta-rechazo-dte" />
      </div>


      {/* NC Pendientes Widget */}
      <Card data-testid="nc-pendientes-widget">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-bold text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> NC/ND Pendientes de Aplicar
              {ncData && ncData.en_alerta > 0 && (
                <span className="ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-600 text-white animate-pulse">
                  {ncData.en_alerta} en alerta
                </span>
              )}
            </CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-1 rounded">
                Alerta: &gt;{ncData?.dias_alerta_nc || diasConfig} días
              </span>
              <button onClick={() => setShowConfig(!showConfig)} className="p-1.5 rounded hover:bg-gray-100 border border-gray-200" data-testid="nc-config-btn" title="Configurar días de alerta">
                <Settings className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {showConfig && (
            <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="nc-config-panel">
              <p className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1.5">
                <Settings className="w-3.5 h-3.5" /> Configuración de Alerta NC
              </p>
              <div className="flex items-end gap-3">
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Días para marcar en alerta</label>
                  <Input type="number" min="1" max="365" value={diasConfig} onChange={e => setDiasConfig(Number(e.target.value))}
                    className="w-24 text-sm font-bold" data-testid="nc-dias-input" />
                </div>
                <Button size="sm" onClick={saveConfig} disabled={savingConfig}
                  className="bg-amber-600 hover:bg-amber-700 text-xs" data-testid="nc-save-config">
                  {savingConfig ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Guardar'}
                </Button>
                <p className="text-[10px] text-gray-500 leading-tight max-w-[240px]">
                  NC/ND sin asociar a factura por más de <strong>{diasConfig}</strong> días se marcarán en rojo. Configura también notificación email en <Link to="/configuracion/notificaciones" className="text-amber-700 underline">Notificaciones</Link>.
                </p>
              </div>
            </div>
          )}

          {ncLoading ? (
            <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
          ) : ncData && ncData.pendientes.length > 0 ? (
            <>
              <div className="flex gap-3 mb-3 text-xs">
                <span className="px-2 py-1 rounded bg-red-50 text-red-700 font-medium">
                  {ncData.total_pendientes} pendientes
                </span>
                <span className="px-2 py-1 rounded bg-red-50 text-red-700 font-bold">
                  -${fmtNum(ncData.monto_total)}
                </span>
                <span className="px-2 py-1 rounded bg-gray-100 text-gray-600">
                  Alerta: &gt;{ncData.dias_alerta_nc} días
                </span>
              </div>
              <table className="w-full text-xs" data-testid="nc-pendientes-table">
                <thead>
                  <tr className="bg-red-50 text-left">
                    <th className="px-3 py-2 font-semibold">Días</th>
                    <th className="px-3 py-2 font-semibold">Tipo</th>
                    <th className="px-3 py-2 font-semibold">Folio</th>
                    <th className="px-3 py-2 font-semibold">Proveedor</th>
                    <th className="px-3 py-2 text-right font-semibold">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {ncData.pendientes.map(nc => (
                    <tr key={nc.folio} className={`border-t ${nc.en_alerta ? 'bg-red-50/60' : 'hover:bg-gray-50/50'}`}>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${
                          nc.en_alerta ? 'bg-red-600 text-white animate-pulse' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {nc.dias_pendiente}d
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold ${
                          nc.tipo_dte === '61' ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-orange-100 text-orange-700 border border-orange-200'
                        }`}>
                          {nc.tipo_dte === '61' ? 'NC' : 'ND'}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono font-bold text-indigo-700">{nc.folio}</td>
                      <td className="px-3 py-2 truncate max-w-[180px]">{nc.emisor_razon_social}</td>
                      <td className="px-3 py-2 text-right font-mono text-red-600 font-bold">-${fmtNum(nc.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Link to="/contabilidad/registro-dte" className="mt-2 inline-flex items-center gap-1 text-xs text-red-600 hover:underline">
                Ver Registro DTEs <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </>
          ) : (
            <p className="text-xs text-green-600 py-3 text-center">Todas las NC/ND están asociadas a facturas</p>
          )}
        </CardContent>
      </Card>

      {data.urgentes_sii?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold text-red-700 flex items-center gap-2">
              <ShieldAlert className="w-4 h-4" /> Facturas por vencer plazo SII
            </CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-red-50 text-left">
                  <th className="px-3 py-2 font-semibold">Plazo</th>
                  <th className="px-3 py-2 font-semibold">Folio</th>
                  <th className="px-3 py-2 font-semibold">Proveedor</th>
                  <th className="px-3 py-2 text-right font-semibold">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.urgentes_sii.map(u => (
                  <tr key={u.folio} className="border-t hover:bg-red-50/50">
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${u.dias_restantes <= 1 ? 'bg-red-600 text-white' : 'bg-orange-100 text-orange-700'}`}>
                        {u.dias_restantes}d
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono font-bold">{u.folio}</td>
                    <td className="px-3 py-2">{u.proveedor}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmt(u.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Link to="/contabilidad/acepta-rechazo-dte" className="mt-2 inline-flex items-center gap-1 text-xs text-red-600 hover:underline">
              Ir a Acepta/Rechaza <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Alerta temprana: OCs aprobadas sin factura */}
      <OCSinFacturaAlertWidget testId="oc-sin-factura-widget-contabilidad" />
    </div>
  );
}
